import React, { useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORY_LABELS } from "./docConstants";
import { Loader2 } from "lucide-react";

const KINDS = [
  { value: "client", label: "Client" }, { value: "family_member", label: "Family member" },
  { value: "external_account", label: "External partner / vendor / facility" },
  { value: "professional", label: "Lawyer / professional" },
  { value: "temporary_guest", label: "Temporary guest" }, { value: "lead", label: "Lead" },
];
const PRIORITIES = [{ value: "low", label: "Low" }, { value: "normal", label: "Normal" }, { value: "high", label: "High" }, { value: "urgent", label: "Urgent" }];

export default function RequestDialog({ open, onOpenChange, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ category: "other", title: "", description: "", requested_from_kind: "", priority: "normal", due_date: "", requested_from_id: "", client_user_id: "", referral_id: "", meeting_id: "", external_account_id: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.title.trim()) return toast.error("Please enter a title.");
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v != null));
      const { data } = await api.post("/doc-center/documents/request", payload);
      toast.success("Document request created.");
      onOpenChange(false);
      setF({ category: "other", title: "", description: "", requested_from_kind: "", priority: "normal", due_date: "", requested_from_id: "", client_user_id: "", referral_id: "", meeting_id: "", external_account_id: "" });
      onCreated?.(data.document);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="doc-request-dialog">
        <DialogHeader><DialogTitle className="text-[#1C4358]">Request a document</DialogTitle>
          <DialogDescription className="text-[12px]">Ask a client, partner, or professional to upload a document. They'll be able to see and fulfil the request from their portal.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-[12px]">Title *</Label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Front & back yard photos" className="h-9 text-[13px]" data-testid="req-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px]">Category</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="h-9 text-[13px]" data-testid="req-category"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[12px]">Requested from</Label>
              <Select value={f.requested_from_kind || "none"} onValueChange={(v) => set("requested_from_kind", v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 text-[13px]" data-testid="req-from-kind"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Internal only</SelectItem>{KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px]">Priority</Label>
              <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="h-9 text-[13px]" data-testid="req-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[12px]">Due date</Label>
              <Input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className="h-9 text-[13px]" data-testid="req-due" />
            </div>
          </div>
          <div>
            <Label className="text-[12px]">Description</Label>
            <Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} className="text-[13px]" data-testid="req-desc" placeholder="What should the person upload?" />
          </div>
          <details className="rounded-lg border border-[#E6EDF2] p-2.5">
            <summary className="text-[12px] font-medium text-[#1C4358] cursor-pointer">Link to a record (optional)</summary>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {[["client_user_id", "Client user ID"], ["referral_id", "Referral ID"], ["meeting_id", "Meeting ID"], ["external_account_id", "External account ID"]].map(([k, lbl]) => (
                <div key={k}><Label className="text-[11px] text-[#6B7280]">{lbl}</Label>
                  <Input value={f[k]} onChange={(e) => set(k, e.target.value)} className="h-8 text-[12px]" data-testid={`req-${k}`} /></div>
              ))}
            </div>
          </details>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="h-9 bg-[#1C4358] hover:bg-[#153341] text-white" data-testid="req-submit">
            {saving ? <Loader2 size={15} className="animate-spin mr-1" /> : null} Create request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
